import { createEntityAdapter, createSlice, } from '@reduxjs/toolkit';
import { deleteMessage, getConversation } from './thunks/conversationThunks';
const assistantMessagesAdapter = createEntityAdapter({
    sortComparer: (a, b) => a.timestamp.localeCompare(b.timestamp),
});
const initialState = {
    messages: {},
};
const assistantMessageSlice = createSlice({
    name: 'messages',
    initialState,
    reducers: {
        addMessage: (state, action) => {
            var _a;
            const conversationId = action.payload.conversation_id;
            state.messages[conversationId] = assistantMessagesAdapter.addOne((_a = state.messages[conversationId]) !== null && _a !== void 0 ? _a : assistantMessagesAdapter.getInitialState(), action.payload);
        },
        updateMessage: (state, action) => {
            var _a;
            const conversationId = action.payload.conversation_id;
            if (!conversationId)
                return;
            state.messages[conversationId] = assistantMessagesAdapter.updateOne((_a = state.messages[conversationId]) !== null && _a !== void 0 ? _a : assistantMessagesAdapter.getInitialState(), {
                id: action.payload.id,
                changes: action.payload,
            });
        },
        appendMessageContent: (state, action) => {
            const { messageCompositeID, content } = action.payload;
            let conversationId = null;
            for (const [cId, conversationState] of Object.entries(state.messages)) {
                if (conversationState.entities[messageCompositeID]) {
                    conversationId = cId;
                    break;
                }
            }
            if (!conversationId) {
                console.error('🚨 [messageSlice] appendMessageContent: Entity not found!', {
                    messageCompositeID: messageCompositeID,
                    searchedEntityId: messageCompositeID,
                    availableConversations: Object.keys(state.messages),
                    availableMessages: Object.keys(state.messages)
                        .map((cId) => Object.keys(state.messages[cId].entities))
                        .flat(),
                });
                return;
            }
            const existingMessage = state.messages[conversationId].entities[messageCompositeID];
            if (existingMessage) {
                state.messages[conversationId] = assistantMessagesAdapter.updateOne(state.messages[conversationId], {
                    id: messageCompositeID,
                    changes: { content: existingMessage.content + content },
                });
            }
        },
        updateMessageMeta: (state, action) => {
            const { id, meta } = action.payload;
            let conversationId = null;
            for (const [cId, conversationState] of Object.entries(state.messages)) {
                if (conversationState.entities[id]) {
                    conversationId = cId;
                    break;
                }
            }
            if (!conversationId)
                return;
            const existingMessage = state.messages[conversationId].entities[id];
            if (existingMessage) {
                const currentMeta = existingMessage.meta || {};
                let updatedMeta = Object.assign({}, currentMeta);
                if (meta.tool_calls && currentMeta.tool_calls) {
                    updatedMeta.tool_calls = [
                        ...currentMeta.tool_calls,
                        ...meta.tool_calls,
                    ];
                }
                else if (meta.tool_calls) {
                    updatedMeta.tool_calls = meta.tool_calls;
                }
                if (meta.sse && currentMeta.sse) {
                    updatedMeta.sse = [...currentMeta.sse, ...meta.sse];
                }
                else if (meta.sse) {
                    updatedMeta.sse = meta.sse;
                }
                if (meta.tool_result !== undefined) {
                    updatedMeta.tool_result = meta.tool_result;
                }
                state.messages[conversationId] = assistantMessagesAdapter.updateOne(state.messages[conversationId], {
                    id,
                    changes: { meta: updatedMeta },
                });
            }
        },
        appendSSEEvent: (state, action) => {
            const { messageId, sseEvent } = action.payload;
            let conversationId = null;
            for (const [cId, conversationState] of Object.entries(state.messages)) {
                if (conversationState.entities[messageId]) {
                    conversationId = cId;
                    break;
                }
            }
            if (!conversationId) {
                console.error('🚨 [messageSlice] appendSSEEvent: Message not found!', {
                    messageId,
                    sseEventType: sseEvent.type,
                    availableConversations: Object.keys(state.messages),
                    availableMessages: Object.keys(state.messages)
                        .map((cId) => Object.keys(state.messages[cId].entities))
                        .flat(),
                });
                return;
            }
            const existingMessage = state.messages[conversationId].entities[messageId];
            if (existingMessage) {
                const currentMeta = existingMessage.meta || {};
                const currentSSE = currentMeta.sse || [];
                const updatedMeta = Object.assign(Object.assign({}, currentMeta), { sse: [...currentSSE, sseEvent] });
                state.messages[conversationId] = assistantMessagesAdapter.updateOne(state.messages[conversationId], {
                    id: messageId,
                    changes: { meta: updatedMeta },
                });
            }
        },
        removeMessage: (state, action) => {
            const { messageId, conversationId } = action.payload;
            if (state.messages[conversationId]) {
                state.messages[conversationId] = assistantMessagesAdapter.removeOne(state.messages[conversationId], messageId);
            }
        },
        replaceMessageId: (state, action) => {
            const { conversationId, oldId, newId } = action.payload;
            if (!state.messages[conversationId]) {
                console.error('[messageSlice] replaceMessageId: Conversation not found');
                return;
            }
            const message = state.messages[conversationId].entities[oldId];
            if (!message) {
                console.error('[messageSlice] replaceMessageId: Message not found', {
                    oldId,
                });
                return;
            }
            const updatedMessage = Object.assign(Object.assign({}, message), { id: newId });
            state.messages[conversationId] = assistantMessagesAdapter.removeOne(state.messages[conversationId], oldId);
            state.messages[conversationId] = assistantMessagesAdapter.addOne(state.messages[conversationId], updatedMessage);
        },
        replaceLatestTempUserMessageId: (state, action) => {
            const { conversationId, newId } = action.payload;
            if (!state.messages[conversationId]) {
                console.error('[messageSlice] replaceLatestTempUserMessageId: Conversation not found');
                return;
            }
            const allMessages = Object.values(state.messages[conversationId].entities)
                .filter((msg) => msg != null)
                .filter((msg) => msg.role === 'user' && msg.id.startsWith('tmp_'));
            const sortedMessages = allMessages.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
            const tempUserMessage = sortedMessages[0];
            if (!tempUserMessage) {
                console.error('[messageSlice] replaceLatestTempUserMessageId: No temp user message found');
                return;
            }
            const updatedMessage = Object.assign(Object.assign({}, tempUserMessage), { id: newId });
            state.messages[conversationId] = assistantMessagesAdapter.removeOne(state.messages[conversationId], tempUserMessage.id);
            state.messages[conversationId] = assistantMessagesAdapter.addOne(state.messages[conversationId], updatedMessage);
        },
        clearMessages: (state, action) => {
            const conversationId = action.payload;
            if (state.messages[conversationId]) {
                state.messages[conversationId] = assistantMessagesAdapter.removeAll(state.messages[conversationId]);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getConversation.fulfilled, (state, action) => {
            var _a;
            const conversation = action.payload;
            if (conversation.messages && conversation.messages.length > 0) {
                state.messages[conversation.id] = assistantMessagesAdapter.setAll((_a = state.messages[conversation.id]) !== null && _a !== void 0 ? _a : assistantMessagesAdapter.getInitialState(), conversation.messages);
            }
            else {
                state.messages[conversation.id] =
                    assistantMessagesAdapter.getInitialState();
            }
        })
            .addCase(deleteMessage.fulfilled, (state, action) => {
            const { messageId, conversationId } = action.payload;
            if (state.messages[conversationId]) {
                state.messages[conversationId] = assistantMessagesAdapter.removeOne(state.messages[conversationId], messageId);
            }
        });
    },
});
export const { addMessage, updateMessage, updateMessageMeta, appendMessageContent, removeMessage, replaceMessageId, replaceLatestTempUserMessageId, clearMessages, appendSSEEvent, } = assistantMessageSlice.actions;
export const assistantMessageReducer = assistantMessageSlice.reducer;
export const messagesSelectors = (conversationId) => assistantMessagesAdapter.getSelectors((state) => state.messages.messages[conversationId]);
export { assistantMessagesAdapter, assistantMessageSlice };
